/**
 * Confirmation Dialog
 * Confirm deactivate/reactivate user actions
 */

import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Member } from '../../services/api/userService';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  user: Member | null;
  action: 'deactivate' | 'reactivate';
  isLoading?: boolean;
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  text-align: center;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  color: ${({ theme }) => theme.colors.status.warning.text};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
`;

const UserInfo = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgrounds.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  text-align: left;
`;

const UserInfoLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

const UserInfoValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const UserInfoEmail = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  user,
  action,
  isLoading = false,
}: ConfirmationDialogProps) => {
  if (!user) return null;

  const isDeactivate = action === 'deactivate';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant={isDeactivate ? 'danger' : 'primary'}
            onClick={onConfirm}
            isLoading={isLoading}
          >
            {isDeactivate ? 'Deactivate' : 'Reactivate'}
          </Button>
        </>
      }
    >
      <Content>
        <IconWrapper>
          <AlertTriangle />
        </IconWrapper>

        <Title>{isDeactivate ? 'Deactivate User?' : 'Reactivate User?'}</Title>

        <Description>
          {isDeactivate
            ? 'This user will lose access to the platform immediately. They will not be able to sign in or perform any actions. You can reactivate them later if needed.'
            : 'This user will regain access to the platform and be able to sign in and perform actions based on their assigned roles.'}
        </Description>

        <UserInfo>
          <UserInfoLabel>User Details</UserInfoLabel>
          <UserInfoValue>
            {user.firstName} {user.lastName}
          </UserInfoValue>
          <br />
          <UserInfoEmail>
            {user.email}
          </UserInfoEmail>
        </UserInfo>
      </Content>
    </Modal>
  );
};
